





































































































import { DetalhesDoPagamento, Pagamento } from '@/models/venda/Pagamento';
import { formatarMoeda } from '@/shareds/formatadores';
import type { PagamentoServiceAdapter } from '@/usecases';
import { Vue, Component, Ref, Watch, Prop } from 'vue-property-decorator'
import { Inject } from 'inversify-props'
import AlertModule from '@/store/vuex/aplicacao/AlertModule';
import type { ResumoDaVenda } from '@/models'
import { imprimirComprovanteDeCancelamentoDoPagamento } from '@/shareds/impressao-comprovente-de-cancelamento-do-pagamento';
import Confirmacao from '../ui/Confirmacao.vue';

const FORMAS_TEF = ['TEF Crédito', 'TEF Débito', 'TEF Pix']

interface ItemTef {
	pagamento: Pagamento
	estornado: boolean
}

@Component({
	components: { Confirmacao },
})
export default class DialogoDeCancelarPagamentos extends Vue {
	@Ref() botaoDeFechar?: { $el: HTMLButtonElement }
	@Prop() venda!: ResumoDaVenda

	@Inject('PagamentoServiceAdapter')
	private pagamentoService!: PagamentoServiceAdapter

	formatarMoeda = formatarMoeda
	resolveDialogo: ((value: boolean) => void) | null = null

	mostra: boolean | null = null
	carregando = false
	itensTef: ItemTef[] = []

	contadorTef = 0
	aguardandoPagamento = false
	mostraAguardandoPagamento = false
	situacaoPagamento = ''
	formaDePagamentoSelecionado = ''
	tentativasEfetuarTef = 0
	retornoAdquirente: string | null = null
	comprovante: string | null = null

	@Watch('mostra')
	async findPagamentos() {
		if (!this.mostra || !this.venda.id) return
		try {
			this.carregando = true
			const anterioresEstornados = this.itensTef.filter(i => i.estornado)

			const todos = await this.pagamentoService.findPagamentosDetalhadosByVendaId(this.venda.id)
			const tef = todos.filter(p => FORMAS_TEF.includes(p.tipoDePagamento.formaDePagamento))

			const novosItens = await Promise.all(
				tef.map(async (pagamento): Promise<ItemTef> => {
					const jaEstornado = anterioresEstornados.find(i => i.pagamento.id === pagamento.id)
					if (jaEstornado) return jaEstornado

					if (!pagamento.detalhesDoPagamentoId) {
						return { pagamento, estornado: false }
					}
					try {
						const detalhes = await this.pagamentoService.buscarDetalhesDoPagamento(
							pagamento.detalhesDoPagamentoId,
						)
						return { pagamento, estornado: detalhes.statusDoPagamento === 'ESTORNADO' }
					} catch {
						return { pagamento, estornado: false }
					}
				}),
			)

			// Reincorpora itens estornados que o backend não retorna mais
			const novosIds = new Set(tef.map(p => p.id))
			const perdidos = anterioresEstornados.filter(i => !novosIds.has(i.pagamento.id))
			this.itensTef = [...novosItens, ...perdidos]
		} catch (e) {
			AlertModule.setError(e)
		} finally {
			this.carregando = false
		}
	}

	@Watch('mostraAguardandoPagamento')
	onMostraAguardandoPagamento() {
		if (this.comprovante) {
			imprimirComprovanteDeCancelamentoDoPagamento(this.comprovante)
			this.comprovante = null
		}
	}

	obterSubtituloDeConfirmacao(pagamento: Pagamento) {
		const parcelas = pagamento.qtdeDeParcelas ? ` em ${pagamento.qtdeDeParcelas}x` : ''
		return `R$ ${formatarMoeda(pagamento.valor)} ${pagamento.tipoDePagamento.nome}${parcelas}`
	}

	iniciarCancelamento(pagamento: Pagamento) {
		const config = this.venda.pontoDeVenda?.loja?.configuracaoDaLoja
		if (!config || (!config.integracaoControlPayAtiva && !config.integracaoSitefAtiva)) {
			AlertModule.setError('Nenhuma integração TEF configurada para este PDV')
			return
		}

		this.formaDePagamentoSelecionado = pagamento.tipoDePagamento.nome
		this.situacaoPagamento = 'Cancelamento Iniciado'
		this.mostraAguardandoPagamento = true

		if (config.integracaoControlPayAtiva) {
			this.cancelarPagamentoTef(pagamento)
		} else {
			this.cancelarPagamentoSitef(pagamento)
		}
	}

	marcarComoEstornado(pagamento: Pagamento) {
		const idx = this.itensTef.findIndex(i => i.pagamento.id === pagamento.id)
		if (idx !== -1) {
			this.itensTef.splice(idx, 1, { ...this.itensTef[idx], estornado: true })
		}
	}

	cancelarPagamentoTef(pagamento: Pagamento) {
		setTimeout(async () => {
			this.contadorTef++

			if (pagamento.detalhesDoPagamentoId !== null) {
				if (this.contadorTef === 1) {
					if (!this.venda) return
					try {
						const detalhes = await this.pagamentoService.cancelarPagamentoTef(
							this.venda.pontoDeVenda.id,
							pagamento.detalhesDoPagamentoId,
						)
						this.situacaoPagamento = this.validarStatusPagamentoTefDescricao(detalhes)
						this.cancelarPagamentoTef(pagamento)
						this.comprovante = detalhes.comprovanteAdquirente
					} catch (error: any) {
						this.zerarPagamentoTef()
						AlertModule.setError(error)
					}
				} else {
					try {
						if (!this.venda) throw new Error('Turno de venda não encontrado')
						const detalhesPagamento = await this.pagamentoService.consultarPagamentoTef(
							this.venda.pontoDeVenda.id,
							pagamento.detalhesDoPagamentoId,
						)
						this.retornoAdquirente = detalhesPagamento.msgAdquirente
						this.situacaoPagamento = this.validarStatusPagamentoTefDescricao(detalhesPagamento)

						if (this.validarStatusPagamentoTefFinalizado(detalhesPagamento)) {
							if (this.validarStatusCancelamentoTefAprovado(detalhesPagamento)) {
								this.marcarComoEstornado(pagamento)
							}
							setTimeout(() => this.zerarPagamentoTef(), 5000)
						} else {
							this.cancelarPagamentoTef(pagamento)
						}
						this.comprovante = detalhesPagamento.comprovanteAdquirente
					} catch (error: any) {
						AlertModule.setError(error)
						this.zerarPagamentoTef()
					}
				}
			} else {
				this.zerarPagamentoTef()
				AlertModule.setError(
					'Pagamento não pode ser cancelado, pois não possui pagamento com TEF relacionado',
				)
			}
		}, 5000)
	}

	zerarPagamentoTef() {
		this.aguardandoPagamento = false
		this.mostraAguardandoPagamento = false
		this.contadorTef = 0
		this.situacaoPagamento = ''
		this.retornoAdquirente = null
		this.tentativasEfetuarTef = 0
	}

	validarStatusCancelamentoTefAprovado(detalhesDoPagamento: DetalhesDoPagamento) {
		return detalhesDoPagamento.status === '20'
	}

	validarStatusPagamentoTefDescricao(detalhesDoPagamento: DetalhesDoPagamento) {
		switch (detalhesDoPagamento.status) {
			case '5':
			case '6':
				this.aguardandoPagamento = true
				return 'Aguardando pagamento, insira ou aproxime o cartão'
			case '10':
				return 'Pagamento finalizado'
			case '15':
			case '25':
				return 'Pagamento recusado'
			case '18':
			case '19':
				this.aguardandoPagamento = true
				return 'Aguardando cancelamento'
			case '20':
				return 'Pagamento cancelado'
			default:
				return 'Situação do pagamento indefinida'
		}
	}

	validarStatusPagamentoTefFinalizado(detalhesDoPagamento: DetalhesDoPagamento) {
		switch (detalhesDoPagamento.status) {
			case '3':
			case '5':
			case '6':
			case '18':
			case '19':
			case '30':
				return false
			default:
				return true
		}
	}

	async mostrar(vendaDetalhes): Promise<boolean> {
		this.venda = vendaDetalhes
		this.mostra = true
		return new Promise(resolve => this.resolveDialogo = resolve)
	}

	cancelarPagamentoSitef(pagamento: Pagamento) {
		setTimeout(async () => {
			this.contadorTef++

			if (pagamento.detalhesDoPagamentoId !== null) {
				if (this.contadorTef === 1) {
					if (!this.venda) return
					try {
						const detalhes = await this.pagamentoService.cancelarPagamentoSitef(
							this.venda.pontoDeVenda.id,
							pagamento,
						)
						this.situacaoPagamento = this.validarStatusPagamentoSiTefDescricao(detalhes)
						this.cancelarPagamentoSitef(pagamento)
						this.comprovante = detalhes.comprovanteAdquirente
					} catch (error: any) {
						this.zerarPagamentoTef()
						AlertModule.setError(error)
					}
				} else {
					try {
						if (!this.venda) throw new Error('Turno de venda não encontrado')
						const detalhesPagamento = await this.pagamentoService.buscarDetalhesDoPagamento(
							pagamento.detalhesDoPagamentoId,
						)
						this.retornoAdquirente = detalhesPagamento.msgAdquirente
						this.situacaoPagamento = this.validarStatusPagamentoSiTefDescricao(detalhesPagamento)

						if (this.validarStatusPagamentoTefFinalizado(detalhesPagamento)) {
							if (this.validarStatusCancelamentoTefAprovado(detalhesPagamento)) {
								this.marcarComoEstornado(pagamento)
							}
							setTimeout(() => this.zerarPagamentoTef(), 5000)
						} else {
							this.cancelarPagamentoSitef(pagamento)
						}
						this.comprovante = detalhesPagamento.comprovanteAdquirente
					} catch (error: any) {
						AlertModule.setError(error)
						this.zerarPagamentoTef()
					}
				}
			} else {
				this.zerarPagamentoTef()
				AlertModule.setError(
					'Pagamento não pode ser cancelado, pois não possui pagamento com TEF relacionado',
				)
			}
		}, 5000)
	}

	validarStatusPagamentoSiTefDescricao(detalhesDoPagamento: DetalhesDoPagamento) {
		switch (detalhesDoPagamento.status) {
			case '3':
				this.aguardandoPagamento = true
				return detalhesDoPagamento.msgAdquirente
			case '5':
			case '6':
				this.aguardandoPagamento = true
				return 'Aguardando pagamento'
			case '10':
				return 'Pagamento finalizado'
			case '15':
			case '25':
				return 'Pagamento recusado'
			case '18':
			case '19':
				this.aguardandoPagamento = true
				return 'Aguardando cancelamento'
			case '20':
				return 'Pagamento cancelado'
			default:
				return 'Situação do pagamento indefinida'
		}
	}
}
